import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { ttsService } from '../services/tts';
import {
  ketThemes,
  ketStudyPlan,
  ketExamInfo,
  ketTotalWords,
  getDailyChallenge,
  getWordsByTheme,
  getRandomQuizWords,
  KETWord,
  KETTheme,
  WeekPlan,
} from '../data/ket-vocabulary';

interface KETTrainingPageProps {
  user: UserProgress;
  onNavigate?: (page: string) => void; // Optional navigation callback
}

type ViewMode = 'overview' | 'theme' | 'study' | 'quiz' | 'plan';

export default function KETTrainingPage({ user, onNavigate }: KETTrainingPageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedTheme, setSelectedTheme] = useState<KETTheme | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [showMeaning, setShowMeaning] = useState(false);
  const [quizWords, setQuizWords] = useState<KETWord[]>([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<string[]>([]); // Store selected word instead of index
  const [quizFinished, setQuizFinished] = useState(false);
  const [studyProgress, setStudyProgress] = useState<Record<string, number>>({});
  const [isPlaying, setIsPlaying] = useState(false); // Track playing state
  const [quizOptions, setQuizOptions] = useState<KETWord[]>([]); // Store quiz options to prevent reshuffling

  // Load progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('ket_study_progress');
    if (saved) {
      setStudyProgress(JSON.parse(saved));
    }
  }, []);

  // Save progress to localStorage
  const saveProgress = (themeId: string, index: number) => {
    const newProgress = { ...studyProgress, [themeId]: index };
    setStudyProgress(newProgress);
    localStorage.setItem('ket_study_progress', JSON.stringify(newProgress));
  };

  // Play word pronunciation
  const handlePlayWord = async (word: string) => {
    if (isPlaying) {
      // If already playing, stop
      ttsService.stop();
      setIsPlaying(false);
      return;
    }

    try {
      setIsPlaying(true);

      // Check if TTS is initialized
      const currentVoice = ttsService.getCurrentVoice();
      if (!currentVoice) {
        console.warn('TTS not fully initialized, attempting to play anyway...');
      }

      await ttsService.speakSlowly(word);
      setIsPlaying(false);
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);

      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('not-allowed')) {
        alert('浏览器阻止了音频播放。请点击页面其他地方后再尝试播放。');
      } else {
        alert('播放失败。请刷新页面或检查浏览器设置。');
      }
    }
  };

  // Get learned words count
  const getLearnedCount = () => {
    return Object.values(studyProgress).reduce((sum, count) => sum + count, 0);
  };

  // Start studying a theme
  const startStudy = (theme: KETTheme) => {
    console.log('🎯 startStudy called with theme:', theme.id, theme.name);
    setSelectedTheme(theme);
    const progress = studyProgress[theme.id] || 0;
    setCurrentWordIndex(progress);
    setShowMeaning(false);
    setViewMode('study');
    console.log('✅ startStudy: viewMode set to "study", selectedTheme set');
  };

  // Generate quiz options for current question
  const generateQuizOptions = (currentWord: KETWord, allWords: KETWord[]) => {
    const wrongAnswers = allWords
      .filter(w => w.word !== currentWord.word)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    const options = [currentWord, ...wrongAnswers].sort(() => Math.random() - 0.5);
    return options;
  };

  // Start quiz
  const startQuiz = (theme: KETTheme) => {
    setSelectedTheme(theme);
    const words = getRandomQuizWords(theme.id, 10);
    setQuizWords(words);
    setCurrentQuizIndex(0);
    setQuizAnswers([]);
    setQuizFinished(false);
    // Generate initial options for first question
    const allWords = getWordsByTheme(theme.id);
    if (words.length > 0 && allWords.length >= 4) {
      setQuizOptions(generateQuizOptions(words[0], allWords));
    }
    setViewMode('quiz');
  };

  // Handle next word in study mode
  const handleNextWord = () => {
    if (!selectedTheme) return;
    const words = getWordsByTheme(selectedTheme.id);
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
      setShowMeaning(false);
      saveProgress(selectedTheme.id, currentWordIndex + 1);
    }
  };

  // Handle previous word
  const handlePrevWord = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(currentWordIndex - 1);
      setShowMeaning(false);
    }
  };

  // Handle quiz answer
  const handleQuizAnswer = (selectedWord: string) => {
    setQuizAnswers([...quizAnswers, selectedWord]);
    if (currentQuizIndex < quizWords.length - 1) {
      // Generate options for next question
      const nextIndex = currentQuizIndex + 1;
      const allWords = getWordsByTheme(selectedTheme!.id);
      if (allWords.length >= 4) {
        setQuizOptions(generateQuizOptions(quizWords[nextIndex], allWords));
      }
      setCurrentQuizIndex(nextIndex);
    } else {
      setQuizFinished(true);
    }
  };

  // Calculate quiz score
  const calculateScore = () => {
    let correct = 0;
    quizAnswers.forEach((selectedWord, index) => {
      if (selectedWord === quizWords[index].word) correct++;
    });
    return correct;
  };

  // Daily challenge
  const dailyChallenge = getDailyChallenge();

  // Render overview
  const renderOverview = () => (
    <div className="space-y-8 animate-slide-up">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            if (onNavigate) {
              onNavigate('home');
            } else {
              window.history.back();
            }
          }}
          className="p-2 rounded-xl bg-white/80 text-gray-600 hover:bg-white transition-all shadow-soft flex items-center gap-1"
        >
          <span className="text-lg">←</span>
          <span className="text-sm font-medium">返回主页</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">🎯 KET 剑桥英语备考</h1>
            <p className="text-white/80 text-sm sm:text-base">Cambridge English: Key (A2 Level)</p>
          </div>
          <div className="text-5xl sm:text-6xl animate-float">🎓</div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-center">
          <div className="bg-white/20 rounded-xl p-2 sm:p-3">
            <p className="text-lg sm:text-2xl font-bold">{ketTotalWords}</p>
            <p className="text-xs sm:text-sm text-white/80">总词汇</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 sm:p-3">
            <p className="text-lg sm:text-2xl font-bold">{getLearnedCount()}</p>
            <p className="text-xs sm:text-sm text-white/80">已学习</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 sm:p-3">
            <p className="text-lg sm:text-2xl font-bold">{ketThemes.length}</p>
            <p className="text-xs sm:text-sm text-white/80">主题数</p>
          </div>
          <div className="bg-white/20 rounded-xl p-2 sm:p-3">
            <p className="text-lg sm:text-2xl font-bold">{ketStudyPlan.length}</p>
            <p className="text-xs sm:text-sm text-white/80">周计划</p>
          </div>
        </div>
      </div>

      {/* Daily Challenge */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-6 shadow-soft">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{dailyChallenge.icon}</span>
            <div>
              <p className="text-lg font-bold text-gray-700">📅 今日挑战</p>
              <p className="text-sm text-gray-500">{dailyChallenge.name} · {dailyChallenge.description}</p>
              <p className="text-xs text-gray-600 mt-1">建议时间: {dailyChallenge.duration}分钟</p>
            </div>
          </div>
          <button
            onClick={() => setViewMode('plan')}
            className="px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-all"
          >
            开始挑战 →
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => setViewMode('study')}
          className="bg-white rounded-2xl p-6 shadow-soft card-hover text-center"
        >
          <span className="text-4xl mb-2 block">📚</span>
          <p className="font-bold text-gray-700">词汇学习</p>
          <p className="text-sm text-gray-500">按主题学习</p>
        </button>
        <button
          onClick={() => setViewMode('quiz')}
          className="bg-white rounded-2xl p-6 shadow-soft card-hover text-center"
        >
          <span className="text-4xl mb-2 block">✍️</span>
          <p className="font-bold text-gray-700">词汇测验</p>
          <p className="text-sm text-gray-500">随机测试</p>
        </button>
        <button
          onClick={() => setViewMode('plan')}
          className="bg-white rounded-2xl p-6 shadow-soft card-hover text-center"
        >
          <span className="text-4xl mb-2 block">📅</span>
          <p className="font-bold text-gray-700">学习计划</p>
          <p className="text-sm text-gray-500">8周计划</p>
        </button>
        <button
          onClick={() => alert('考试信息功能开发中...')}
          className="bg-white rounded-2xl p-6 shadow-soft card-hover text-center"
        >
          <span className="text-4xl mb-2 block">ℹ️</span>
          <p className="font-bold text-gray-700">考试信息</p>
          <p className="text-sm text-gray-500">KET介绍</p>
        </button>
      </div>

      {/* All Themes */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">📖 所有主题</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {ketThemes.map((theme) => {
            const progress = studyProgress[theme.id] || 0;
            const progressPercent = Math.round((progress / theme.wordCount) * 100);
            return (
              <button
                key={theme.id}
                onClick={() => startStudy(theme)}
                className="bg-white rounded-2xl p-4 shadow-soft card-hover text-left"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{theme.icon}</span>
                  <span className="font-bold text-gray-700">{theme.name}</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{theme.wordCount} 词</p>
                <div className="progress-bar h-2">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">{progressPercent}% 完成</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Study Plan Preview */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">📅 8周学习计划</h2>
        <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
          <div className="divide-y divide-gray-100">
            {ketStudyPlan.slice(0, 4).map((week) => (
              <button
                key={week.week}
                onClick={() => setViewMode('plan')}
                className="w-full p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="font-bold text-indigo-600">{week.week}</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-700">第{week.week}周: {week.focus}</p>
                      <p className="text-sm text-gray-500">{week.themeNames.join(' + ')} · {week.words}词</p>
                    </div>
                  </div>
                  <span className="text-gray-400">→</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => setViewMode('plan')}
          className="w-full mt-4 py-3 bg-indigo-100 text-indigo-600 rounded-xl font-medium hover:bg-indigo-200 transition-all"
        >
          查看完整计划 →
        </button>
      </div>
    </div>
  );

  // Render theme selection for study/quiz
  const renderThemeSelection = () => (
    <div className="space-y-6 animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setViewMode('overview')}
          className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all"
        >
          ← 返回
        </button>
        <h2 className="text-2xl font-bold text-gray-800">
          {viewMode === 'study' ? '选择学习主题' : '选择测验主题'}
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {ketThemes.map((theme) => {
          const progress = studyProgress[theme.id] || 0;
          return (
            <button
              key={theme.id}
              onClick={() => viewMode === 'study' ? startStudy(theme) : startQuiz(theme)}
              className="bg-white rounded-2xl p-6 shadow-soft card-hover text-center"
            >
              <span className="text-4xl mb-2 block">{theme.icon}</span>
              <p className="font-bold text-gray-700 mb-1">{theme.name}</p>
              <p className="text-sm text-gray-500">{theme.wordCount} 词</p>
              {viewMode === 'study' && progress > 0 && (
                <p className="text-xs text-indigo-600 mt-2">已学 {progress} 词</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Render study mode
  const renderStudy = () => {
    if (!selectedTheme) {
      console.error('❌ renderStudy: No selectedTheme');
      return null;
    }

    console.log('✅ renderStudy: Selected theme:', selectedTheme.id, selectedTheme.name);
    const words = getWordsByTheme(selectedTheme.id);
    console.log('📚 Words loaded:', words.length, words.slice(0, 3));

    // Safety check: ensure we have words
    if (words.length === 0) {
      console.error('❌ No words found for theme:', selectedTheme.id);
      return (
        <div className="text-center py-8">
          <p className="text-red-600">该主题暂无词汇数据</p>
          <p className="text-sm text-gray-500 mt-2">主题ID: {selectedTheme.id}</p>
          <button
            onClick={() => {
              setSelectedTheme(null);
              setViewMode('study');
            }}
            className="mt-4 px-6 py-3 bg-gray-200 rounded-xl"
          >
            返回
          </button>
        </div>
      );
    }

    const currentWord = words[currentWordIndex];
    console.log('📖 Current word:', currentWordIndex, currentWord);

    if (!currentWord) {
      console.error('❌ Invalid word index:', currentWordIndex, 'of', words.length);
      return (
        <div className="text-center py-8">
          <p className="text-red-600">词汇索引错误</p>
          <p className="text-sm text-gray-500 mt-2">
            索引 {currentWordIndex} 超出范围 (0-{words.length - 1})
          </p>
          <button
            onClick={() => {
              setCurrentWordIndex(0);
              setSelectedTheme(null);
              setViewMode('study');
            }}
            className="mt-4 px-6 py-3 bg-gray-200 rounded-xl"
          >
            返回
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              setSelectedTheme(null);
              setViewMode('study');
            }}
            className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all"
          >
            ← 返回
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{selectedTheme.icon}</span>
            <span className="font-bold text-gray-700">{selectedTheme.name}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl p-4 shadow-soft">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              {currentWordIndex + 1} / {words.length}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round(((currentWordIndex + 1) / words.length) * 100)}%
            </span>
          </div>
          <div className="progress-bar h-3">
            <div
              className="progress-bar-fill bg-indigo-500"
              style={{ width: `${((currentWordIndex + 1) / words.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Word Card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
          <div className="mb-6">
            <button
              onClick={() => handlePlayWord(currentWord.word)}
              className={`text-5xl font-bold text-indigo-600 hover:text-indigo-800 transition-all cursor-pointer ${
                isPlaying ? 'animate-pulse text-indigo-800' : ''
              }`}
            >
              {currentWord.word}
            </button>
            <button
              onClick={() => handlePlayWord(currentWord.word)}
              className={`mt-4 text-3xl transition-transform ${
                isPlaying ? 'animate-bounce scale-125' : 'hover:scale-110'
              }`}
              title={isPlaying ? '点击停止播放' : '点击播放发音'}
            >
              {isPlaying ? '🔊' : '🔇'}
            </button>
            {isPlaying && (
              <p className="text-sm text-indigo-600 mt-2 animate-pulse">正在播放...</p>
            )}
          </div>

          {/* Show/Hide Meaning */}
          <div className="mb-6">
            {!showMeaning ? (
              <button
                onClick={() => setShowMeaning(true)}
                className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-all"
              >
                显示释义 👁️
              </button>
            ) : (
              <div className="space-y-4 animate-slide-up">
                <div className="bg-indigo-50 rounded-2xl p-4">
                  <p className="text-xl font-medium text-gray-700">{currentWord.meaning}</p>
                </div>
                <div className="bg-green-50 rounded-2xl p-4">
                  <p className="text-lg text-gray-600">
                    <span className="font-medium">例句:</span> {currentWord.example}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handlePrevWord}
              disabled={currentWordIndex === 0}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                currentWordIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← 上一个
            </button>
            <button
              onClick={() => handlePlayWord(currentWord.word)}
              className={`px-8 py-3 text-white rounded-xl font-bold transition-all ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                  : 'bg-indigo-500 hover:bg-indigo-600'
              }`}
            >
              {isPlaying ? '⏹️ 停止' : '🔊 播放'}
            </button>
            <button
              onClick={handleNextWord}
              disabled={currentWordIndex === words.length - 1}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                currentWordIndex === words.length - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-500 text-white hover:bg-indigo-600'
              }`}
            >
              下一个 →
            </button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => startQuiz(selectedTheme)}
            className="px-6 py-3 bg-orange-100 text-orange-600 rounded-xl font-medium hover:bg-orange-200 transition-all"
          >
            ✍️ 开始测验
          </button>
          <button
            onClick={() => {
              setCurrentWordIndex(0);
              setShowMeaning(false);
            }}
            className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-all"
          >
            🔄 重置
          </button>
        </div>
      </div>
    );
  };

  // Render quiz mode
  const renderQuiz = () => {
    if (!selectedTheme) return null;

    if (quizFinished) {
      const score = calculateScore();
      const percentage = Math.round((score / quizWords.length) * 100);

      return (
        <div className="space-y-6 animate-slide-up text-center">
          <h2 className="text-2xl font-bold text-gray-800">测验完成！</h2>

          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <div className="text-6xl mb-4">
              {percentage >= 80 ? '🎉' : percentage >= 60 ? '😊' : '💪'}
            </div>
            <p className="text-4xl font-bold text-indigo-600 mb-2">
              {score} / {quizWords.length}
            </p>
            <p className="text-xl text-gray-600 mb-4">正确率: {percentage}%</p>

            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <p className="text-sm text-gray-600">
                {percentage >= 80
                  ? '太棒了！继续保持！'
                  : percentage >= 60
                  ? '不错的成绩！继续努力！'
                  : '加油！多练习几次会更好！'}
              </p>
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => startQuiz(selectedTheme)}
                className="px-6 py-3 bg-indigo-500 text-white rounded-xl font-bold hover:bg-indigo-600 transition-all"
              >
                再测一次
              </button>
              <button
                onClick={() => {
                  setSelectedTheme(null);
                  setViewMode('quiz');
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all"
              >
                返回主题选择
              </button>
            </div>
          </div>
        </div>
      );
    }

    const currentWord = quizWords[currentQuizIndex];

    // Get all words for this theme
    if (!selectedTheme) {
      return (
        <div className="text-center py-8">
          <p className="text-red-600">未选择主题</p>
          <button
            onClick={() => {
              setSelectedTheme(null);
              setViewMode('quiz');
            }}
            className="mt-4 px-6 py-3 bg-gray-200 rounded-xl"
          >
            返回
          </button>
        </div>
      );
    }

    const allWords = getWordsByTheme(selectedTheme.id);

    // Use pre-generated options from state
    const options = quizOptions;

    // Safety check: ensure we have enough words and options
    if (allWords.length < 4) {
      console.error('Not enough words for quiz:', allWords.length);
      return (
        <div className="text-center py-8">
          <p className="text-red-600">词汇数量不足，无法进行测验</p>
          <p className="text-sm text-gray-500 mt-2">该主题只有 {allWords.length} 个词汇，至少需要 4 个</p>
          <button
            onClick={() => {
              setSelectedTheme(null);
              setViewMode('quiz');
            }}
            className="mt-4 px-6 py-3 bg-gray-200 rounded-xl"
          >
            返回
          </button>
        </div>
      );
    }

    // Generate options if not available (fallback)
    if (options.length === 0) {
      setQuizOptions(generateQuizOptions(currentWord, allWords));
      return null; // Will re-render with options
    }

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              setSelectedTheme(null);
              setViewMode('quiz');
            }}
            className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all"
          >
            ← 返回
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{selectedTheme.icon}</span>
            <span className="font-bold text-gray-700">{selectedTheme.name} 测验</span>
          </div>
          <span className="text-sm text-gray-600">
            {currentQuizIndex + 1} / {quizWords.length}
          </span>
        </div>

        {/* Progress */}
        <div className="progress-bar h-2">
          <div
            className="progress-bar-fill bg-orange-500"
            style={{ width: `${((currentQuizIndex + 1) / quizWords.length) * 100}%` }}
          />
        </div>

        {/* Quiz Card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl">
          <div className="text-center mb-6">
            <button
              onClick={() => handlePlayWord(currentWord.word)}
              className={`text-4xl font-bold text-indigo-600 hover:text-indigo-800 transition-all cursor-pointer ${
                isPlaying ? 'animate-pulse text-indigo-800' : ''
              }`}
            >
              {currentWord.word}
            </button>
            <button
              onClick={() => handlePlayWord(currentWord.word)}
              className={`ml-2 text-2xl transition-transform ${
                isPlaying ? 'animate-bounce scale-125' : 'hover:scale-110'
              }`}
              title={isPlaying ? '点击停止播放' : '点击播放发音'}
            >
              {isPlaying ? '🔊' : '🔇'}
            </button>
            <p className="text-lg text-gray-500 mt-2">
              选择正确的中文释义
              {isPlaying && <span className="ml-2 text-indigo-600 animate-pulse">(正在播放...)</span>}
            </p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleQuizAnswer(option.word)}
                className="p-4 bg-gray-50 rounded-2xl text-left hover:bg-indigo-50 hover:border-indigo-200 border-2 border-transparent transition-all"
              >
                <p className="text-lg text-gray-700">{option.meaning}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render study plan
  const renderPlan = () => (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setViewMode('overview')}
          className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all"
        >
          ← 返回
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">📅 8周学习计划</h2>
        <div></div>
      </div>

      {/* Plan overview */}
      <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-3xl p-6 shadow-soft">
        <div className="text-center">
          <p className="text-lg font-bold text-gray-700 mb-2">KET 假期冲刺计划</p>
          <p className="text-sm text-gray-600">适合小学四年级 · {ketTotalWords}词 · 8周完成</p>
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            <div className="bg-white/60 rounded-xl p-2">
              <p className="font-bold text-indigo-600">60-90</p>
              <p className="text-xs text-gray-600">分钟/天</p>
            </div>
            <div className="bg-white/60 rounded-xl p-2">
              <p className="font-bold text-indigo-600">{ketTotalWords}</p>
              <p className="text-xs text-gray-600">总词汇</p>
            </div>
            <div className="bg-white/60 rounded-xl p-2">
              <p className="font-bold text-indigo-600">8</p>
              <p className="text-xs text-gray-600">周计划</p>
            </div>
            <div className="bg-white/60 rounded-xl p-2">
              <p className="font-bold text-indigo-600">{getLearnedCount()}</p>
              <p className="text-xs text-gray-600">已学习</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly plans */}
      <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
        <div className="divide-y divide-gray-100">
          {ketStudyPlan.map((week) => (
            <div key={week.week} className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-lg">
                  {week.week}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-bold text-gray-700">第{week.week}周</p>
                    <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-1 rounded-full">
                      {week.words}词
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">主题:</span> {week.themeNames.join(' + ')}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">重点:</span> {week.focus}
                  </p>
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-2 mt-2">
                    💡 {week.tips}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    {week.themeIds.map((themeId) => {
                      const theme = ketThemes.find(t => t.id === themeId);
                      if (!theme) return null;
                      return (
                        <button
                          key={themeId}
                          onClick={() => startStudy(theme)}
                          className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-sm hover:bg-indigo-100 transition-all"
                        >
                          {theme.icon} {theme.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Daily challenge */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-6 shadow-soft">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{dailyChallenge.icon}</span>
            <div>
              <p className="font-bold text-gray-700">📅 今日挑战</p>
              <p className="text-sm text-gray-600">{dailyChallenge.name}</p>
              <p className="text-xs text-gray-500">{dailyChallenge.description} · {dailyChallenge.duration}分钟</p>
            </div>
          </div>
          {dailyChallenge.type === 'vocabulary' && (
            <button
              onClick={() => setViewMode('study')}
              className="px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-all"
            >
              开始学习
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Main render
  console.log('🔍 Main render - viewMode:', viewMode, 'selectedTheme:', selectedTheme?.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50 pb-8">
      {viewMode === 'overview' && renderOverview()}
      {(viewMode === 'study' && !selectedTheme) && renderThemeSelection()}
      {(viewMode === 'quiz' && !selectedTheme) && renderThemeSelection()}
      {viewMode === 'study' && selectedTheme && renderStudy()}
      {viewMode === 'quiz' && selectedTheme && renderQuiz()}
      {viewMode === 'plan' && renderPlan()}
    </div>
  );
}